import { FC, useEffect, useMemo, useState } from 'react';

import { Permission } from '@/api/permissions';
import { Input, Loader, Modal, theme, Typography } from '@/shared/design-system';
import type { CatalogGroup } from '@/shared/modules/role-board/types';
import { countSelectedInCatalog, PermissionSelectionMap } from '@/utils/permission-catalog';

import { CenteredState, ModalBody } from '../styled';
import { useAugmentedCatalogGroups } from '../use-augmented-catalog-groups';
import { PermissionPicker } from './permission-picker';

export type RoleCreateModalProps = {
  open: boolean;
  catalogGroups?: readonly CatalogGroup[];
  canGrantProtected: boolean;
  isPending?: boolean;
  onConfirm: (_name: string, _permissions: Permission[]) => void;
  onClose: () => void;
};

export const RoleCreateModal: FC<RoleCreateModalProps> = ({
  open,
  catalogGroups,
  canGrantProtected,
  isPending = false,
  onConfirm,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [selection, setSelection] = useState<PermissionSelectionMap>(new Map());

  useEffect(() => {
    if (open) {
      setName('');
      setSelection(new Map());
    }
  }, [open]);

  const present = useMemo(() => new Set(selection.keys()), [selection]);
  const groups = useAugmentedCatalogGroups({ groups: catalogGroups, present });
  const permissions = useMemo(() => [...selection.keys()], [selection]);
  const canSubmit = name.trim().length > 0 && catalogGroups !== undefined;

  const changePermission = ({ permission }: { permission: Permission }, granted: boolean) => {
    setSelection((current) => {
      const next = new Map(current);
      if (granted) next.set(permission, { locked: false });
      else next.delete(permission);
      return next;
    });
  };

  const changeGroup = (
    _group: CatalogGroup,
    add: { permission: Permission }[],
    remove: Permission[],
  ) => {
    add.forEach((permission) => changePermission(permission, true));
    remove.forEach((permission) => changePermission({ permission }, false));
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title="New role"
      description="Choose a name and permission set for the new custom role."
      primaryAction={{
        label: isPending ? 'Submitting…' : 'Create role',
        disabled: !canSubmit || isPending,
        onClick: () => canSubmit && onConfirm(name.trim(), permissions),
      }}
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
    >
      <ModalBody>
        <Input
          size="md"
          label="Role name"
          value={name}
          placeholder="e.g. Fund operations"
          onChange={(event) => setName(event.target.value)}
        />
        {catalogGroups === undefined ? (
          <CenteredState>
            <Loader />
          </CenteredState>
        ) : groups.length === 0 ? (
          <Typography variant="body3" color={theme.colors.text.secondary}>
            No permissions are available for this role.
          </Typography>
        ) : (
          <PermissionPicker
            groups={groups}
            selection={selection}
            canGrantProtected={canGrantProtected}
            editable={!isPending}
            onChange={changePermission}
            onBulkChange={changeGroup}
            summary={`${countSelectedInCatalog(groups, selection)} permissions requested`}
          />
        )}
      </ModalBody>
    </Modal>
  );
};
